import metaCache from "flow/utils/metaCache";
import config from "flow/config";
import WebModuleEntry from "flow/manifests/WebModuleEntry";
import ProductEntry from "flow/manifests/ProductEntry";
import MyFlow from "flow/MyFlow";
import environment from "flow/configuration/environment";
import { IProductEntry } from "./IProductEntry";
import ProductEntryFlow3 from "./ProductEntryFlow3";
import { Dictionary } from "flow/api/types";
import { IProductEntryMeta } from "./IProductEntryMeta";
import { IProductEntryMetaFlow3 } from "./IProductEntryMetaFlow3";

function getProducts(myFlow: MyFlow): IProductEntry[] {
    const productDefinitions: IProductEntry[] = [];
    const products = metaCache.lookup<Dictionary<IProductEntryMeta>>(config.metaKeys.products, null);
    const serverRuntimeContext = environment.get<string>(environment.keys.runtimeContext);

    if (products !== null) {
        Object.keys(products).forEach(productName => {
            try {
                const entry = new ProductEntry(products[productName]);
                if (entry.isActive && isRuntimeContextRelevant(entry.runtimeContext, serverRuntimeContext)) {
                    productDefinitions.push(entry);
                } else {
                    myFlow.debug("Excluded inactive product " + productName);
                }
            } catch (e) {
                myFlow.warn(e.message);
            }
        });
    }
    return productDefinitions;
}

function getProductsFlow3(myFlow: MyFlow): IProductEntry[] {
    const productDefinitions: IProductEntry[] = [];
    const products = metaCache.lookup<Dictionary<IProductEntryMetaFlow3>>(config.metaKeys.productsFlow3, null);
    const serverRuntimeContext = environment.get<string>(environment.keys.runtimeContext);

    if (products !== null) {
        Object.keys(products).forEach(productName => {
            try {
                const entry = new ProductEntryFlow3(products[productName]);
                if (entry.isActive && isRuntimeContextRelevant(entry.runtimeContext, serverRuntimeContext)) {
                    productDefinitions.push(entry);
                } else {
                    myFlow.debug("Excluded inactive product " + productName);
                }
            } catch (e) {
                myFlow.warn(e.message);
            }
        });
    }
    return productDefinitions;
}

function getWebModules(myFlow: MyFlow): WebModuleEntry[] {
    const moduleDefinitions: WebModuleEntry[] = [];
    const webModules = metaCache.lookup<Dictionary<Dictionary>>(config.metaKeys.webModules, null);
    const serverRuntimeContext = environment.get<string>(environment.keys.runtimeContext);

    if (webModules !== null) {
        // We will exclude FlowHtml.WebModules.FlowLog that only contains a loglevel value to avoid a false warning.
        // Ideally it should be moved to FlowHtml.Settings, but that would reset any configuration made out there.
        // Key is lowercased in the meta-object, so we need to compared with lowercased value.
        const flowLogKey = config.moduleKeyName.toLocaleLowerCase();
        Object.keys(webModules)
            .filter(key => key !== flowLogKey)
            .forEach(webModuleName => {
                try {
                    const entry = new WebModuleEntry(webModuleName, webModules[webModuleName]);
                    if (entry.isActive && isRuntimeContextRelevant(entry.runtimeContext, serverRuntimeContext)) {
                        moduleDefinitions.push(entry);
                    } else {
                        myFlow.debug("Excluded inactive web module " + webModuleName);
                    }
                } catch (e) {
                    myFlow.warn(e.message);
                }
            });
    }
    return moduleDefinitions;
}

function isRuntimeContextRelevant(runtimeContext: string, serverRuntimeContext: string) {
    let moduleContexts: string[];

    // Default to public if no runtime contexts specified
    if (isNullEmptyOrUndefined(runtimeContext)) {
        moduleContexts = ["public"];
    } else {
        moduleContexts = splitTrimAndLowerCase(runtimeContext);
    }

    const serverContexts = splitTrimAndLowerCase(serverRuntimeContext);

    // Intersection
    const matchedRoles = moduleContexts.filter(context => serverContexts.indexOf(context) >= 0);

    return matchedRoles.length > 0;
}

function isNullEmptyOrUndefined(value: any) {
    return value === undefined || value === null || value === "";
}

function splitTrimAndLowerCase(runtimeContext: string) {
    return runtimeContext.split(",").map(value => value.trim().toLowerCase());
}

export default {
    getProducts,
    getProductsFlow3,
    getWebModules
};
